import $ from 'jquery';

import { patch } from 'bootstrap/load-snabbdom';
import { VNode } from 'snabbdom/vnode';

import { Skin } from 'lib/vendor/terrific/Application';

import { fetcher } from 'lib/util/helpers';
import { getProfile, Profile } from 'lib/personalization/profile';

import { Address, ServiceAvailabilityData, isValidResponse } from 'lib/ecommerce/Tc.Module.ServiceAvailability';
import OrderOverlayOrderButtons, { OrderButtonsInitData } from 'lib/ecommerce/Tc.Module.OrderOverlayOrderButtons';

import LegoContainer, { ServiceId, CurrentSelection } from '../Tc.Module.LegoContainer';
import { Module } from 'lib/vendor/terrific/Module';

type FlavourName = 'internetFlavour' | 'tvFlavour' | 'voipFlavour';

const PAYLOAD_NAMES : Record<string, FlavourName> = {
	internet: 'internetFlavour',
	tv: 'tvFlavour',
	voip: 'voipFlavour',
};

class OrderButtons {
	private withLoginText : string;
	private withoutLoginText : string;
	public profile : Promise<Profile>;
	private availabilityData? : ServiceAvailabilityData;
	private withoutloginUrl? : string;
	private root : VNode | HTMLElement;

	constructor(
		public mod : DOCPCheckoutSkin,
		buttonsModule : OrderOverlayOrderButtons,
	) {
		this.withLoginText = buttonsModule.$ctx.find('.orderButtonLinkText').text() || buttonsModule.$ctx.find('.existing-customer .button').text();
		this.withoutLoginText = buttonsModule.$ctx.find('.nologinButtonLinkText').text() || buttonsModule.$ctx.find('.without-login .button').text();
		this.profile = getProfile();
		this.root = buttonsModule.$ctx.find('> .button-group')[0];
		this.update();
	}

	public setAvailability(data? : ServiceAvailabilityData) {
		this.availabilityData = data;
		this.update();
	}

	public setWithoutLoginUrl(url? : string) {
		this.withoutloginUrl = url;
		this.update();
	}

	private update() {
		this.root = patch(
			this.root,
			this.render(),
		);
	}

	private render() : VNode {
		if(!this.availabilityData) {
			return <div/>;
		}

		return <div>
			<sdx-button-group>
				{this.withoutloginUrl ? <sdx-button
					props-theme="secondary"
					props-href={this.withoutloginUrl}
					props-label={this.withoutLoginText}
				/> : undefined}
				<sdx-button
					on-click={[this.sendToCheckout.bind(this)]}
					props-label={this.withLoginText}
				/>
			</sdx-button-group>
		</div>;
	}

	private async sendToCheckout(_event : MouseEvent, button : HTMLButtonElement) {
		button.disabled = true;
		try {
			const checkoutUrl = await this.mod.getCheckoutUrl(
				this.availabilityData,
				`${window.location.pathname}${window.location.search}${window.location.hash}`,
				true,
			);
			window.location.href = checkoutUrl;
		} catch(e) {
			this.mod.log.error('Error sending to DOCP checkout', e);
		} finally {
			button.disabled = false;
		}
	}
}

export interface Product {
	skuCode : string;
	siebelProductName : string;
}

interface DOCPCheckoutSkin extends LegoContainer {
	docpProductMapping : Promise<Record<string, Product>>;
	orderButtons : OrderButtons[];

	replaceOrderButtons() : void;
	getCheckoutUrl(availabilityData : ServiceAvailabilityData | undefined, returnUrl : string, forceLogin : boolean) : Promise<string>;
	onServiceAvailabilityDataUpdate(response : ServiceAvailabilityData) : void;
	onOrderButtonsInit(data : OrderButtonsInitData) : void;
}

// tslint:disable-next-line: variable-name
export const DOCPCheckout : Skin<LegoContainer, DOCPCheckoutSkin> = function DOCPCheckout(parent) {
	this.on = function on(callback) {
		this.docpProductMapping = loadDOCPProductMapping();
		this.replaceOrderButtons();
		this.sandbox.subscribe('serviceavailabilitycheck', this);
		this.sandbox.subscribe('orderoverlaystatus', this);
		parent.on.call(this, callback);
	};

	this.replaceOrderButtons = function replaceOrderButtons() {
		this.orderButtons = this.$ctx
			.find('.modOrderOverlayOrderButtons')
			.map((_i, button) => (this.sandbox.getModuleInstance(button, 'OrderOverlayOrderButtons')) as OrderOverlayOrderButtons)
			.get()
			.filter(Boolean)
			.map(buttonsModule => new OrderButtons(this, buttonsModule));
	};

	this.onServiceAvailabilityDataUpdate = function onServiceAvailabilityDataUpdate(response) {
		let res : ServiceAvailabilityData | undefined = response;
		if(!isValidResponse(response)) {
			this.log.warn('Invalid ServiceAvailabilityDataUpdate', response);
			res = undefined;
		}
		this.log.debug('ServiceAvailabilityDataUpdate', response);
		for(const buttons of this.orderButtons) {
			buttons.setAvailability(res);
		}
	};

	this.onOrderButtonsInit = function onOrderButtonsInit(data) {
		this.log.debug('orderButtonsInit', data);
		for(const buttons of this.orderButtons) {
			buttons.setWithoutLoginUrl(data.noLoginUrl);
		}
	};

	this.getCheckoutUrl = async function getCheckoutUrl(availabilityData, returnUrl, forceLogin) {
		if(!availabilityData) {
			throw new Error('Availability data not present');
		}

		const currentSelection = this.currentSelection;
		if(!currentSelection) {
			throw new Error('Config not present');
		}

		const mapping = await this.docpProductMapping;
		const flavours = mapFlavours(mapping, currentSelection, this);

		let scn : string | undefined;

		const profile = await getProfile();
		const user = profile.getUser();
		if(user) {
			scn = user.getId();
		}

		return urlToCheckout(flavours, availabilityData.address, returnUrl, scn, forceLogin);
	};

	async function urlToCheckout(flavours : Record<FlavourName, Product>, address : Address, returnUrl : string, scn? : string, forceLogin = false) {
		const payload = {
			address: {
				city: address.city,
				houseName: address.houseName,
				houseNumber: address.houseNumber,
				locationId: address.locationId,
				street: address.street,
				zip: address.zipCode4,
			},
			bundle: {
				siebelProductName: 'LEGO Home',
				skuCode: 'LEGO_Home',
			},
			cancelUrl : returnUrl,
			returnUrl,
			scn: scn ?? null,
			sessionChannel: 'selfcare',
			uiLanguage: document.documentElement.lang,
			...flavours,
		};

		const fetch = await fetcher();
		const response = await fetch('/oce/soe/wbc/common/v1/order', {
			body: JSON.stringify(payload),
			headers: {
				consumer: 'OCE',
				'content-type': 'application/json',
				...xsrfHeader(),
			},
			method: 'POST',
		});

		if(!response.headers.get('content-type')?.startsWith('text/plain')) {
			throw new Error(`Unexpected response ${await response.text()}`);
		}

		let url = await response.text();

		if(forceLogin && !scn) {
			// Using online instead of guest will force the user to log in
			url = url.replace(/\/guest\//, '/online/');
		}

		return url;
	}
};

let mappingPromise : Promise<Record<string, Product>> | undefined;

export function loadDOCPProductMapping() : Promise<Record<string, Product>> {
	if(!mappingPromise) {
		mappingPromise = (async() => {
			const fetch = await fetcher();
			const response = await fetch('/oce/soe/products/v1/category/wireline/subscriptions?category=WIRELINE');
			const data = await response.json() as Product[];

			const result : Record<string, Product> = {};

			for(const product of data) {
				result[product.siebelProductName] = product;
			}

			return result;

		})();
	}

	return mappingPromise;
}

export function mapFlavours(mapping : Record<string, Product>, selection : CurrentSelection, callingMod : Module) {
	const flavours = {} as any as Record<FlavourName, Product>;

	for(const service in PAYLOAD_NAMES) {
		const product = selection[service as ServiceId];
		if(!product) {
			// It’s a product DOCP checkout does not support (e.g. mobile)
			continue;
		}
		const siebelId = product.slideId;
		if(!(siebelId in mapping)) {
			callingMod.log.warn('Mapping from Siebel ID', siebelId, 'to SKU code not found in', mapping);
			continue;
		}
		flavours[PAYLOAD_NAMES[service]] = {
			siebelProductName: siebelId,
			skuCode: mapping[siebelId].skuCode,
		};
	}

	return flavours;
}

export function xsrfHeader() : Record<string, string> {
	const xsrfValue = $.cookie('OCE-XSRF-TOKEN') as string;
	if(!xsrfValue) {
		return {};
	}

	return {'X-OCE-XSRF-TOKEN': xsrfValue};
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/skins/DOCPCheckout.tsx